import { forwardRef, type CSSProperties } from 'react'
import { AlertTriangle, Globe, Plus, RotateCw, Search, X } from 'lucide-react'
import { useLocale } from '../i18n/context'
import { DECK_TOP_STRIP_CLEARANCE } from './DeckTopStrip'

/**
 * What the server said about the current query. The four states are the four
 * things §15 asks the surface to be able to say, and they are distinct on
 * purpose — in particular `error` is NOT collapsed into `empty`.
 *
 * Reporting a failed request as "nothing found" is the precise defect this
 * whole item removes, only worse: the reader is told their word is not in the
 * deck, in the one case where the application has no idea whether it is. A
 * failure that says so is recoverable; a confident wrong answer is not.
 */
export type DeckSearchStatus = 'idle' | 'loading' | 'error' | 'empty' | 'results'

interface Props {
  query: string
  onQueryChange: (q: string) => void
  /** Deck-wide match count from the server — the FOUND denominator (§13). */
  resultTotal: number
  status: DeckSearchStatus
  onRetry: () => void
  /** §15's explicit exit to the global palette, carrying the typed word so the
   * boundary between the two searches can be crossed without retyping. */
  onSearchEverywhere: () => void
  /** §15's second exit from an empty result: put this word in this deck. */
  onAddToDeck: () => void
  /** §15: a search that would land outside an active chip drops the chip, and
   * the surface says so in words with the undo beside it. Never a silent
   * "not found" for a word the deck contains. The chip UI itself lives
   * elsewhere now (`DeckFilterChipsBar`) — this is only the announcement,
   * shown here because the search panel is what the reader is looking at when
   * the drop happens. */
  chipDroppedBySearch: boolean
  onUndoChipDrop: () => void
  onClose: () => void
}

/**
 * The deck's in-deck search panel (brief §15, reworked 2026-08-04).
 *
 * Carries the query input, the result count/status, and an explicit exit —
 * "and nothing else": no filter chips here. Bundling chips into this same
 * block is what made the previous version tall enough that, with the keyboard
 * raised, only a couple of match rows fit below it. Chips are `their own
 * separate affordance` (`DeckFilterChipsBar`, opened from the ⋯ menu) — they
 * were never reported broken at their own, smaller size, so they keep their
 * existing sticky-in-feed positioning unchanged; only search's positioning
 * changes here.
 *
 * `position: fixed` right below the pinned top strip, not sticky-in-flow.
 * Sticky positioning is exactly how the previous version could open
 * partially off-screen: its flow position was wherever the component tree
 * happened to mount it, which is at the mercy of how far the reader had
 * already scrolled. Fixed positioning has no such dependency — the panel is
 * always fully within the viewport the instant it mounts, on every entry
 * (view mode, editing mode, a system deck) without exception.
 *
 * Search here is NAVIGATION AND ONLY NAVIGATION. There is no translate, no
 * speak, no add-from-result, no enrichment — that work belongs to the global
 * palette, which already exists. The separation is carried by the wording,
 * the placeholder and the name (`find in deck` against `search everywhere`),
 * and by a visible exit from one to the other, so a reader who wants the
 * other tool can see that it is a different tool rather than concluding this
 * one is broken.
 *
 * Forwards its ref to the root node: the caller measures nothing from it for
 * layout (this panel reserves no space of its own — see DeckMobileFeed's
 * search-open scroll effect) but does read its `getBoundingClientRect()` once,
 * to align the feed flush against this panel's bottom edge when it opens.
 */
const DeckSearchPanel = forwardRef<HTMLDivElement, Props>(function DeckSearchPanel({
  query, onQueryChange, resultTotal, status, onRetry, onSearchEverywhere, onAddToDeck,
  chipDroppedBySearch, onUndoChipDrop, onClose,
}, ref) {
  const { tr } = useLocale()
  const dm = tr.deckManagement
  const dd = tr.deckDetail
  const searching = status !== 'idle'

  return (
    <div ref={ref} style={panelStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px' }}>
        <div style={fieldStyle}>
          <Search size={15} color="var(--text-secondary)" />
          <input
            autoFocus
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            // "Find in this deck" rather than a bare "Search": the placeholder
            // is where the scope is stated, because it is the text a reader is
            // looking at while deciding what this box can do for them.
            placeholder={dm.searchInDeckPlaceholder}
            style={inputStyle}
          />
          {query && (
            <button onClick={() => onQueryChange('')} aria-label={dd.cancelLabel} style={clearBtnStyle}>
              <X size={14} />
            </button>
          )}
        </div>
        <button onClick={onClose} aria-label={dm.closeSearchAria} style={closeBtnStyle}>
          <X size={16} />
        </button>
      </div>

      {/* §15: the chip was dropped so the search could reach the whole deck.
          Said in words, with the undo beside it. */}
      {chipDroppedBySearch && (
        <div style={noticeRowStyle}>
          <span style={noticeTextStyle}>{dm.chipDroppedForSearch}</span>
          <button onClick={onUndoChipDrop} style={linkBtnStyle}>{dm.undo}</button>
        </div>
      )}

      {searching && (
        <div style={{ padding: '0 12px 10px' }}>
          {status === 'loading' && (
            <span style={statusTextStyle}>{dm.searchRunning}</span>
          )}

          {status === 'results' && (
            <span style={statusTextStyle}>{dm.searchResultCount(resultTotal)}</span>
          )}

          {/* Honest failure. No fallback to filtering the loaded cards: a
              partial answer here is indistinguishable from a complete one. */}
          {status === 'error' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <AlertTriangle size={14} color="var(--danger)" />
              <span style={{ ...statusTextStyle, color: 'var(--danger)' }}>{dm.searchFailed}</span>
              <button onClick={onRetry} style={linkBtnStyle}>
                <RotateCw size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                {dm.searchRetry}
              </button>
            </div>
          )}

          {/* §15's empty result and its two exits — the whole point of naming
              them is that "not here" is a fork, not a dead end. */}
          {status === 'empty' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={statusTextStyle}>{dm.searchNotInThisDeck}</span>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={onSearchEverywhere} style={exitBtnStyle}>
                  <Globe size={13} />
                  {dm.searchEverywhere}
                </button>
                <button onClick={onAddToDeck} style={exitBtnStyle}>
                  <Plus size={13} />
                  {dm.searchAddToDeck}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Available from every non-empty state too: the reader may find three
          hits and still have meant the other tool. */}
      {searching && status !== 'empty' && (
        <div style={{ padding: '0 12px 10px' }}>
          <button onClick={onSearchEverywhere} style={linkBtnStyle}>
            {dm.searchEverywhere}
          </button>
        </div>
      )}
    </div>
  )
})

export default DeckSearchPanel

const panelStyle: CSSProperties = {
  position: 'fixed', left: 0, right: 0, top: DECK_TOP_STRIP_CLEARANCE, zIndex: 24,
  background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)',
  boxShadow: 'var(--shadow-chrome-above)',
}

const fieldStyle: CSSProperties = {
  flex: 1, display: 'flex', alignItems: 'center', gap: 8,
  background: 'var(--bg-secondary)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-pill)', padding: '8px 14px',
}

const inputStyle: CSSProperties = {
  flex: 1, minWidth: 0, border: 'none', background: 'transparent', outline: 'none',
  fontFamily: 'var(--font-ui)', fontSize: 14, color: 'var(--text-primary)', padding: 0,
}

const clearBtnStyle: CSSProperties = {
  background: 'none', border: 'none', color: 'var(--text-secondary)',
  cursor: 'pointer', padding: 0, lineHeight: 1, display: 'flex',
}

const closeBtnStyle: CSSProperties = {
  width: 34, height: 34, borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--border)', background: 'var(--bg-surface)',
  color: 'var(--text-secondary)', display: 'flex', alignItems: 'center',
  justifyContent: 'center', cursor: 'pointer', flexShrink: 0, padding: 0,
}

const statusTextStyle: CSSProperties = {
  fontFamily: 'var(--font-ui)', fontSize: 11.5, color: 'var(--text-secondary)',
}

const noticeRowStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px 10px',
}

const noticeTextStyle: CSSProperties = {
  fontFamily: 'var(--font-ui)', fontSize: 11.5, color: 'var(--text-secondary)', flex: 1,
}

const linkBtnStyle: CSSProperties = {
  background: 'transparent', border: 'none', color: 'var(--accent)',
  fontFamily: 'var(--font-ui)', fontWeight: 600, fontSize: 11.5,
  cursor: 'pointer', padding: 0, textDecoration: 'underline', flexShrink: 0,
}

// The two exits are equals — §15 offers them as a pair, so neither takes a
// filled capsule that would rank one over the other.
const exitBtnStyle: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  background: 'var(--bg-surface)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)',
  padding: '7px 13px', cursor: 'pointer',
  fontFamily: 'var(--font-ui)', fontWeight: 600, fontSize: 12.5,
}
